import { ReactNode, useMemo } from "react";
import { useSelector } from "react-redux";
import { selectEnvironmentInteractionPoints } from "../../../app/slices/EnvironmentSlice";
import { selectSelectedTake3D } from "../../../app/slices/TrainingSlice";
import useSituationHelper from "../../../hooks/SituationHelper.hook";
import NodeInfoField from "./NodeInfoField";
import {
  addEnvironmentPositionDisplayNames,
  getEnvironmentPositionContexts,
  getSituationPositionId,
} from "../EnvironmentPositionContext";

export default function NodeProximityPositionDropdown(props: {
  label: string;
  selectedPositionId: string;
  accessory?: ReactNode;
}) {
  const currentTakeLocation = useSelector(selectSelectedTake3D);
  const allPositions = useSelector(selectEnvironmentInteractionPoints);
  const { getSituationMetadata } = useSituationHelper();

  const positionOptions = useMemo(() => {
    const contexts = getEnvironmentPositionContexts(
      allPositions ?? [],
      currentTakeLocation?.situations,
      getSituationMetadata,
    );

    return addEnvironmentPositionDisplayNames(
      contexts.flatMap((context) => [
        ...context.actorPositions.map((position) => ({
          id: getSituationPositionId(context, position.id),
          sourceId: position.id,
          name: position.name,
          context,
        })),
        ...context.propPositions.map((position) => ({
          id: getSituationPositionId(context, position.id),
          sourceId: position.id,
          name: position.name,
          context,
        })),
      ]),
    );
  }, [allPositions, currentTakeLocation?.situations, getSituationMetadata]);

  const selectedPositionName = useMemo(() => {
    return (
      positionOptions.find(
        (position) =>
          position.id === props.selectedPositionId ||
          position.sourceId === props.selectedPositionId,
      )?.displayName ?? ""
    );
  }, [positionOptions, props.selectedPositionId]);

  return (
    <div className="flex flex-col gap-[8px] noDrag nowheel">
      <div className="font-medium text-creator-text-sub text-[14px]">
        {props.label}
      </div>
      <div className="flex items-center gap-[8px]">
        <div className="flex-1 min-w-0">
          <NodeInfoField
            label=""
            value={selectedPositionName || "No position selected"}
          />
        </div>
        {props.accessory && <div className="shrink-0">{props.accessory}</div>}
      </div>
    </div>
  );
}
